import { useCallback, useState } from "react";
import {
  Box, Stack, Typography, Alert, CircularProgress, Button, Paper, Tabs, Tab,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import {
  getSerieMensual, getSerieMensualDias, ACQ_CMF, matchCuenta, fmtMoney,
  type SerieDiaRow,
} from "../services/monitoreoVivo";
import { listCollectors } from "../services/debines";

// CP-446: informe de compliance CMF — solo collectors de cuenta compartida (los que no
// liquidan en cuenta propia, ej. LaRural queda afuera — mismo filtro `matchCuenta("comp", …)`
// que ya usa Serie Mensual). 100% Fe, sin tocar backend:
// - Pestaña diaria (mes en curso): combina /debines/collectors (CUIT) con el mismo
//   detail=dias por collector que ya usa el drill-down de Serie Mensual.
// - Pestaña mensual (meses cerrados): no pide nada nuevo — reagrupa por CUIT los
//   totales mensuales que ya trae getSerieMensual() para la vista diaria.
const CONCURRENCY = 5;
const MESES_PEDIDOS = 6; // margen para tener varios meses cerrados en la vista mensual

interface FilaDiaria { cuit: string; porDia: Record<number, number> }
interface FilaMensual { cuit: string; porMes: Record<string, number> }

/** Suma de los días cargados en la fila — total del mes en curso hasta la fecha. */
function acumuladoMes(r: FilaDiaria): number {
  return Object.values(r.porDia).reduce((a, b) => a + b, 0);
}

/** Días del mes a mostrar como columna: hasta donde llegó el rollup
 *  (`coverage.through_date`) si es el mes en curso, si no todos los del mes. */
function diasDelMesACubrir(mesEnCurso: string, throughDate: string): number[] {
  const diasEnElMes = new Date(Number(mesEnCurso.slice(0, 4)), Number(mesEnCurso.slice(4, 6)), 0).getDate();
  const ultimo = throughDate.slice(0, 6) === mesEnCurso ? Number(throughDate.slice(6, 8)) : diasEnElMes;
  return Array.from({ length: Math.min(ultimo, diasEnElMes) }, (_, i) => i + 1);
}

async function withLimit<T, R>(items: T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> {
  const out: R[] = new Array(items.length);
  let next = 0;
  async function worker() {
    while (next < items.length) {
      const i = next++;
      out[i] = await fn(items[i]);
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return out;
}

const csvEsc = (v: unknown) => {
  const t = v == null ? "" : String(v);
  return /[",\n;]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
};

const descargarBlob = (lines: string[], filename: string) => {
  const blob = new Blob([String.fromCharCode(0xfeff) + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export default function MonitoreoComplianceCmf() {
  const [tab, setTab] = useState<"diario" | "mensual">("diario");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [err, setErr] = useState<string | null>(null);
  const [mesActual, setMesActual] = useState<string | null>(null);
  const [diasDelMes, setDiasDelMes] = useState<number[]>([]);
  const [mesesCerrados, setMesesCerrados] = useState<string[]>([]);
  const [currency, setCurrency] = useState("ARS");
  const [filasDiarias, setFilasDiarias] = useState<FilaDiaria[] | null>(null);
  const [filasMensuales, setFilasMensuales] = useState<FilaMensual[] | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null); setFilasDiarias(null); setFilasMensuales(null);
    setProgress({ done: 0, total: 0 });
    try {
      // "Mes en curso" lo decide el rollup (coverage.partial_month), no el reloj del
      // browser — mismo criterio que Serie Mensual.
      const serie = await getSerieMensual({ months: MESES_PEDIDOS, acquirer: ACQ_CMF, source: "liquidado" });
      const acq = serie.acquirers.find((a) => a.key === ACQ_CMF);
      if (!acq) { setErr(`No hay datos de ${ACQ_CMF} en Serie Mensual.`); return; }
      const mesEnCurso = serie.coverage.partial_month || serie.months[serie.months.length - 1];
      if (!mesEnCurso) { setErr("No se pudo determinar el mes en curso."); return; }
      setMesActual(mesEnCurso);
      setCurrency(acq.currency);
      const dias = diasDelMesACubrir(mesEnCurso, serie.coverage.through_date || "");
      setDiasDelMes(dias);

      // Solo cuenta compartida: deja afuera a los clientes CMF que liquidan en su
      // propia cuenta (ej. LaRural) — es el mismo filtro que ya existe en Serie Mensual.
      const collectorsCompartida = acq.collectors.filter((c) => matchCuenta("comp", c.account));
      const cuitPorCollector = new Map((await listCollectors()).map((c) => [c.name, c.tax_id]));

      // --- Vista mensual (meses cerrados): no pide nada nuevo, reagrupa por CUIT lo
      // que ya vino en `serie` para los collectors de cuenta compartida.
      const cerrados = serie.months.filter((m) => m !== mesEnCurso);
      setMesesCerrados(cerrados);
      const porCuitMes = new Map<string, Record<string, number>>();
      let sinCuitMensual = 0;
      for (const c of collectorsCompartida) {
        const cuit = cuitPorCollector.get(c.collector);
        const tieneDatos = cerrados.some((m) => c.months[m]);
        if (!cuit) { if (tieneDatos) sinCuitMensual++; continue; }
        const row = porCuitMes.get(cuit) ?? {};
        for (const m of cerrados) {
          const cell = c.months[m];
          if (cell) row[m] = (row[m] ?? 0) + cell.amount;
        }
        porCuitMes.set(cuit, row);
      }
      setFilasMensuales(
        [...porCuitMes.entries()].map(([cuit, porMes]) => ({ cuit, porMes }))
          .sort((a, b) => a.cuit.localeCompare(b.cuit)),
      );

      // --- Vista diaria (mes en curso): 1 llamada por collector de cuenta compartida,
      // con concurrencia acotada para no saturar el Lambda.
      const collectorIds = collectorsCompartida.map((c) => c.collector);
      setProgress({ done: 0, total: collectorIds.length });
      const fallidos: string[] = [];
      const porCollector = await withLimit(collectorIds, CONCURRENCY, async (collector) => {
        try {
          const d = await getSerieMensualDias({ acquirer: ACQ_CMF, collector, month: mesEnCurso, source: "liquidado" });
          return { collector, days: d.days };
        } catch (e: any) {
          // No tragar el error: si una llamada falla, el CUIT de ese collector queda
          // sub-contado sin ningún aviso si no se registra acá.
          console.error(`[compliance-cmf] falló ${collector}:`, e);
          fallidos.push(collector);
          return { collector, days: [] as SerieDiaRow[] };
        } finally {
          setProgress((p) => ({ ...p, done: p.done + 1 }));
        }
      });

      const porCuitDia = new Map<string, Map<string, number>>();
      let sinCuitDiario = 0;
      for (const { collector, days } of porCollector) {
        const cuit = cuitPorCollector.get(collector);
        if (!cuit) { if (days.length) sinCuitDiario++; continue; }
        const dias = porCuitDia.get(cuit) ?? new Map<string, number>();
        for (const dia of days) dias.set(dia.date, (dias.get(dia.date) ?? 0) + dia.amount);
        porCuitDia.set(cuit, dias);
      }

      const avisos: string[] = [];
      if (fallidos.length > 0) {
        avisos.push(`${fallidos.length} collector(es) no se pudieron consultar (quedan sub-contados, no en cero a propósito): ${fallidos.join(", ")}.`);
      }
      const sinCuitTotal = sinCuitDiario + sinCuitMensual;
      if (sinCuitTotal > 0) {
        avisos.push(`${sinCuitTotal} collector(es) de cuenta compartida con movimientos no tienen CUIT configurado (collection_config) — quedan afuera del informe.`);
      }
      if (avisos.length > 0) setErr(avisos.join(" "));

      const filasD: FilaDiaria[] = [...porCuitDia.entries()]
        .map(([cuit, dias]) => {
          const porDia: Record<number, number> = {};
          for (const [fecha, monto] of dias) porDia[Number(fecha.slice(6, 8))] = monto;
          return { cuit, porDia };
        })
        .sort((a, b) => a.cuit.localeCompare(b.cuit));
      setFilasDiarias(filasD);
    } catch (e: any) {
      setErr(e?.message ?? "Error al cargar");
    } finally {
      setLoading(false);
    }
  }, []);

  const descargarDiarioCsv = () => {
    if (!filasDiarias || !mesActual) return;
    const linea = (vals: unknown[]) => vals.map(csvEsc).join(",");
    const lines = [linea(["cuit", ...diasDelMes.map((d) => `${mesIso(mesActual)}-${String(d).padStart(2, "0")}`), "Acumulado mes", "moneda"])];
    for (const r of filasDiarias) {
      lines.push(linea([r.cuit, ...diasDelMes.map((d) => r.porDia[d] ?? ""), acumuladoMes(r), currency]));
    }
    descargarBlob(lines, `liquidacion-cmf-diario_${mesActual}.csv`);
  };

  const descargarMensualCsv = () => {
    if (!filasMensuales) return;
    const linea = (vals: unknown[]) => vals.map(csvEsc).join(",");
    const lines = [linea(["cuit", ...mesesCerrados.map(mesIso), "moneda"])];
    for (const r of filasMensuales) {
      lines.push(linea([r.cuit, ...mesesCerrados.map((m) => r.porMes[m] ?? ""), currency]));
    }
    const tramo = mesesCerrados.length ? `${mesesCerrados[0]}-${mesesCerrados[mesesCerrados.length - 1]}` : "sin-datos";
    descargarBlob(lines, `liquidacion-cmf-mensual_${tramo}.csv`);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Reportes CMF</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Solo collectors de <b>cuenta compartida</b> CMF (los que liquidan en cuenta propia, ej. LaRural, quedan
        afuera). Fuente: misma serie "liquidado" que Monitoreo → Serie mensual, agrupada por CUIT.
      </Typography>

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
          onClick={load} disabled={loading}>
          {loading ? `Cargando… ${progress.done}/${progress.total || "?"} collectors` : "Cargar"}
        </Button>
        {mesActual && <Typography variant="body2" color="text.secondary">Mes en curso: {mesActual}</Typography>}
      </Stack>

      {err && <Alert severity="warning" sx={{ mb: 2 }}>{err}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="diario" label="Diario (mes en curso)" />
        <Tab value="mensual" label="Mensual por CUIT (meses cerrados)" />
      </Tabs>

      {tab === "diario" && (
        <>
          <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={descargarDiarioCsv}
            disabled={!filasDiarias || filasDiarias.length === 0} sx={{ mb: 1 }}>
            Exportar CSV
          </Button>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>CUIT</TableCell>
                  {mesActual && diasDelMes.map((d) => (
                    <TableCell key={d} align="right" sx={{ fontWeight: 700 }}>{d}/{Number(mesActual.slice(4, 6))}</TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Acumulado mes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!filasDiarias ? (
                  <TableRow><TableCell colSpan={diasDelMes.length + 2} align="center" sx={{ py: 4, color: "text.secondary" }}>Tocá "Cargar" para traer los datos.</TableCell></TableRow>
                ) : filasDiarias.length === 0 ? (
                  <TableRow><TableCell colSpan={diasDelMes.length + 2} align="center" sx={{ py: 4, color: "text.secondary" }}>Sin movimientos liquidados este mes en cuenta compartida.</TableCell></TableRow>
                ) : filasDiarias.map((r) => (
                  <TableRow key={r.cuit} hover>
                    <TableCell sx={{ fontFamily: "monospace" }}>{r.cuit}</TableCell>
                    {diasDelMes.map((d) => (
                      <TableCell key={d} align="right">{r.porDia[d] != null ? fmtMoney(r.porDia[d], currency) : "—"}</TableCell>
                    ))}
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtMoney(acumuladoMes(r), currency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {tab === "mensual" && (
        <>
          <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={descargarMensualCsv}
            disabled={!filasMensuales || filasMensuales.length === 0} sx={{ mb: 1 }}>
            Exportar CSV
          </Button>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>CUIT</TableCell>
                  {mesesCerrados.map((m) => (
                    <TableCell key={m} align="right" sx={{ fontWeight: 700 }}>{Number(m.slice(4, 6))}/{m.slice(0, 4)}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {!filasMensuales ? (
                  <TableRow><TableCell colSpan={mesesCerrados.length + 1} align="center" sx={{ py: 4, color: "text.secondary" }}>Tocá "Cargar" para traer los datos.</TableCell></TableRow>
                ) : filasMensuales.length === 0 ? (
                  <TableRow><TableCell colSpan={mesesCerrados.length + 1} align="center" sx={{ py: 4, color: "text.secondary" }}>Sin movimientos liquidados en cuenta compartida.</TableCell></TableRow>
                ) : filasMensuales.map((r) => (
                  <TableRow key={r.cuit} hover>
                    <TableCell sx={{ fontFamily: "monospace" }}>{r.cuit}</TableCell>
                    {mesesCerrados.map((m) => (
                      <TableCell key={m} align="right">{r.porMes[m] != null ? fmtMoney(r.porMes[m], currency) : "—"}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}

/** yyyyMM → "2026-08" (encabezado de CSV: ordena bien en Excel), mismo criterio que
 *  ya usa Serie Mensual para sus exports. */
function mesIso(m: string): string {
  return /^\d{6}$/.test(m) ? `${m.slice(0, 4)}-${m.slice(4)}` : m;
}
